/**
 * Special Prices upsell — the two pieces of behaviour the cart recommendations
 * carousel needs, in one place.
 *
 * The carousel's markup comes from sections/cart-product-recommendations.liquid,
 * but ProductRecommendations injects it with innerHTML (assets/global.js:1536),
 * and a <script> inserted that way never executes. So the drawer
 * (snippets/cart-drawer.liquid) and the cart page (sections/main-cart-items.liquid)
 * each carry their own copy of the carousel's JS, and the section's own copy is
 * dead in both. Three copies of this arithmetic is three chances for one of them
 * to drift and show a price the cart does not charge — so they all call in here
 * instead.
 *
 * Loaded from layout/theme.liquid, before any of those handlers can fire.
 */
(function () {
  'use strict';

  /* Special Prices mode marks its wrapper with the config; no wrapper, no offer. */
  function canvasOf(el) {
    return el && el.closest ? el.closest('[data-sp-pct]') : null;
  }

  /**
   * What to advertise for a variant, or null when the mode is off or the offer
   * is not genuinely cheaper than simply buying the product.
   *
   * Deliberately the twin of the Liquid that renders the card on the server —
   * same MOP fallback, same rounding to whole rupees, same refusal to advertise
   * at or above the normal price. If the two disagree, the price changes the
   * instant a shopper clicks a colour swatch.
   *
   * @param {{price:number, compare_at_price:?number}} variant
   * @param {Element} el  any node inside the card
   * @returns {{mop:number, price:number, save:number}|null}  amounts in paise
   */
  function priceFor(variant, el) {
    var canvas = canvasOf(el);
    if (!canvas) return null;

    var pct = parseInt(canvas.dataset.spPct, 10);
    if (!(pct > 0)) return null;

    /* MRP is the printed maximum (compare-at); MOP is the price it sells for
       today. The struck figure is whichever one the discount is taken from, so
       `strike - price` is always exactly the saving the Shopify discount will
       apply — striking MRP while discounting MOP is what makes a card promise
       a saving the cart never honours. Anything other than an explicit 'mrp'
       means MOP, so a blank or unrecognised setting fails to the safe reading. */
    var mrp = (variant.compare_at_price && variant.compare_at_price > variant.price)
      ? variant.compare_at_price
      : variant.price;
    var strike = canvas.dataset.spBase === 'mrp' ? mrp : variant.price;
    var price = Math.round(strike * (100 - pct) / 100 / 100) * 100;

    /* A "discount" at or above the shelf price is a markup in disguise. */
    if (price >= variant.price) return null;

    /* `pct` is the badge's number: the struck figure is the basis, so the
       saving really is exactly this percentage — no second sum to drift.
       `label` carries the merchant's wording so the swatch-swap path cannot
       print a different word from the one Liquid rendered. */
    return {
      strike: strike,
      price: price,
      save: strike - price,
      pct: pct,
      label: canvas.dataset.spLabel || ''
    };
  }

  /**
   * The /cart/add.js line for an Add button, carrying the line-item property a
   * Shopify discount function reads to tell that the item came from this
   * widget. Falls back to a plain line when the mode is off.
   *
   * @param {string|number} variantId
   * @param {Element} el  the button, or any node inside the card
   */
  function lineItemFor(variantId, el) {
    var line = { id: Number(variantId), quantity: 1 };
    var canvas = canvasOf(el);
    if (canvas && canvas.dataset.spKey) {
      line.properties = {};
      line.properties[canvas.dataset.spKey] = canvas.dataset.spValue || 'true';
    }
    return line;
  }

  /**
   * Money for display. Shopify.formatMoney lives in shopify-money.js, a
   * separately deferred asset — window.Shopify can exist while formatMoney
   * does not. Calling it blind throws a TypeError inside the swatch handler,
   * which would abort that handler BEFORE it retargets the Add button, leaving
   * the button on the previously selected variant while the card shows the new
   * one. So this never throws: it degrades to a plain rupee figure instead.
   *
   * @param {number} cents
   */
  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents)
        .replace(/\.00$/, '')
        .replace('Rs.', '\u20b9')
        .replace('Rs', '\u20b9');
    }
    return '\u20b9' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  window.SpecialPrices = { priceFor: priceFor, lineItemFor: lineItemFor, money: money };
})();
